import { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { convertHtmlToString, convertMessageToHtmlAndTrimSpaces, getQuillTextLength } from '../../utils';
import i18nContext from 'components/i18n-context';
import {
  MAX_MESSAGE_LENGTH,
  MAX_TOPIC_SUBJECT_LENGTH,
  MAX_UPLOADED_FILES_SIZE,
  MESSAGE_CLIENTS_AMOUNT_RESULT,
  SEARCH_BOUND_MINIMUM_LENGTH_CLIENT
} from 'components/constants';
import Loader from 'components/Loader';
import { messageRegExp, prepareFieldErrors } from 'services/utils';
import { getMessageClients } from 'services/requestAgent';
import Button from 'uikit/Button/Button';
import Input from 'uikit/Input/Input';
import CustomAsyncSelect from 'components/CustomAsyncSelect';
import { CheckBox } from 'uikit/CheckBox/CheckBox';
import CustomFileDragAndDrop from 'components/CustomFileDragAndDrop';
import './NewTopicForm.scss';

// Helper function to get error message for alert
const getErrorMessageForAlert = (error) => {
  if (typeof error === 'string') return error;
  if (error?.message) return error.message;
  if (error?.error) return error.error;
  return 'An error occurred';
};

const NewTopicForm = ({
  newTopicData,
  setNewTopicData,
  isLoading,
  error,
  clearError,
  uploadedFiles,
  uploadDocuments,
  removeAttachment,
  onSubmit
}) => {
  const i18n = useContext(i18nContext);
  const serverFieldsErrors = prepareFieldErrors(i18n, error);
  const [messageValue, setMessageValue] = useState(newTopicData?.message_body || '');

  const form = useFormik({
    validateOnChange: false,
    initialValues: {
      client: undefined,
      subject: newTopicData?.subject ? newTopicData?.subject : '',
      message: newTopicData?.message_body ? newTopicData?.message_body : '',
      isAnnouncement: false
    },
    validationSchema: Yup.object({
      client: Yup.object().required(i18n.getMessage('messages.error.client.empty')),
      subject: Yup.string()
        .required(i18n.getMessage('messages.error.subject.empty'))
        .trim()
        .test('not-only-spaces', i18n.getMessage('messages.error.subject.empty'), (value) => {
          return value && value.trim().length > 0;
        })
        .min(1, i18n.getMessage('messages.error.subject.empty'))
        .max(
          MAX_TOPIC_SUBJECT_LENGTH,
          i18n.getMessage('messages.error.subject.maxSize', { size: MAX_TOPIC_SUBJECT_LENGTH })
        )
        .matches(messageRegExp, i18n.getMessage('messages.error.message.invalidFormat')),
      isAnnouncement: Yup.bool(),
      message: Yup.string()
        .required(i18n.getMessage('messages.error.message.empty'))
        .test('not-only-spaces', i18n.getMessage('messages.error.message.empty'), (value) => {
          return value && value.trim().length > 0;
        })
        .max(MAX_MESSAGE_LENGTH, i18n.getMessage('messages.error.message.maxSize', { size: MAX_MESSAGE_LENGTH }))
        .matches(messageRegExp, i18n.getMessage('messages.error.message.invalidFormat'))
    }),
    onSubmit: (values) => {
      const messageBody = convertMessageToHtmlAndTrimSpaces(messageValue);
      const topicData = {
        account_number: values.client.value,
        documents: uploadedFiles.filter(({ id }) => id).map(({ id }) => id),
        message_body: messageBody,
        subject: values.subject.trim(),
        permission: values.isAnnouncement ? 'READ' : 'READ_WRITE'
      };
      setNewTopicData(topicData);
      onSubmit(topicData);
    }
  });

  const { values, errors, handleSubmit, handleChange, validateField, setFieldValue, submitCount } = form;

  useEffect(() => {
    clearError();
    setNewTopicData('', '');
  }, []);

  useEffect(() => {
    setFieldValue('message', convertHtmlToString(messageValue));
  }, [messageValue, setFieldValue]);

  const onBlurSubjectField = () => {
    validateField('subject');
    if (serverFieldsErrors?.subject) {
      clearError();
    }
  };

  const onBlurMessageField = () => {
    validateField('message');
    if (serverFieldsErrors?.message) {
      clearError();
    }
  };

  // Client selector handlers
  const noOptionsMessage = () => i18n.getMessage('messages.newMessage.field.client.noOptionsMessage');
  const loadingMessage = ({ inputValue }) =>
    inputValue.trim()?.length < SEARCH_BOUND_MINIMUM_LENGTH_CLIENT
      ? i18n.getMessage('messages.newMessage.field.client.searchWillStartWith') +
        SEARCH_BOUND_MINIMUM_LENGTH_CLIENT +
        i18n.getMessage('messages.newMessage.field.client.characters')
      : i18n.getMessage('messages.newMessage.field.client.searching');

  const loadOptions = (inputValue) => {
    let trimmedValue = inputValue.trim();
    return (
      trimmedValue?.length >= SEARCH_BOUND_MINIMUM_LENGTH_CLIENT &&
      getMessageClients(trimmedValue, MESSAGE_CLIENTS_AMOUNT_RESULT).then(({ content }) =>
        content.map(({ common_name: name, account_number: number }) => {
          return {
            value: number,
            label: `${name} (${number})`
          };
        })
      )
    );
  };

  const handleClientInputChange = (inputValue) => {
    if (!inputValue) return inputValue;
    return inputValue.replace(/[^a-zA-Z0-9_ @.\-+'"&]/g, '');
  };

  const handleChangeClientOption = (option) => {
    setFieldValue('client', option);
  };

  const handleBlurClientOption = () => {
    validateField('client');
    if (serverFieldsErrors?.client) {
      clearError();
    }
  };

  const handleChangeIsAnnouncement = () => {
    setFieldValue('isAnnouncement', !values.isAnnouncement);
  };

  return (
    <div className={'new-topic-wrapper'}>
      <form action={'#'} id={'send'} onSubmit={handleSubmit} className={'new-topic-form'}>
        <CustomAsyncSelect
          isAutoFocus={true}
          onChange={handleChangeClientOption}
          onBlur={handleBlurClientOption}
          loadList={loadOptions}
          handleInputChange={handleClientInputChange}
          className={'new-topic-form-client-input'}
          name={'client'}
          error={errors?.client || serverFieldsErrors?.client}
          noOptionsMessage={noOptionsMessage}
          loadingMessage={loadingMessage}
          placeholder={i18n.getMessage('messages.newMessage.field.client.placeholder')}
        />
        <Input
          className={'new-topic-form-subject-input'}
          type={'text'}
          autoFocus={false}
          label={i18n.getMessage('messages.form.label.subject')}
          name={'subject'}
          value={values.subject}
          isDisabled={isLoading}
          onChange={handleChange}
          onBlur={onBlurSubjectField}
          initialStatus={submitCount}
          error={errors?.subject || serverFieldsErrors?.subject}
          max={MAX_TOPIC_SUBJECT_LENGTH}
          /* eslint-disable-next-line max-len */
          // symbolsCountText={i18n.getMessage('messages.form.input.message.symbolsLeft', {
          //   amount: MAX_TOPIC_SUBJECT_LENGTH - values.subject.trim().length
          // })}
        />
        <CheckBox
          className={'new-topic-form-announcement-checkbox'}
          isActive={values.isAnnouncement}
          isReverseView={true}
          showCheckBox={true}
          onChange={handleChangeIsAnnouncement}
          value={i18n.getMessage('messages.newMessage.checkbox.announcement.label')}
        />
        <Input
          className={'chat-form-input new-topic-form-editor'}
          type={'text-editor-quill'}
          label={i18n.getMessage('messages.form.input.message.label')}
          placeholder={i18n.getMessage('messages.form.input.message.placeholder')}
          name={'message'}
          isDisabled={isLoading}
          value={messageValue}
          onChange={setMessageValue}
          onBlur={onBlurMessageField}
          initialStatus={submitCount}
          max={MAX_MESSAGE_LENGTH}
          rows={4}
          error={errors?.message || serverFieldsErrors?.message}
          /* eslint-disable-next-line max-len */
          symbolsCountText={i18n.getMessage('messages.form.input.message.symbolsLeft', {
            amount: MAX_MESSAGE_LENGTH - getQuillTextLength(values.message)
          })}
        />

        <CustomFileDragAndDrop
          files={uploadedFiles}
          onChange={uploadDocuments}
          onRemoveFiles={removeAttachment}
          maxSize={MAX_UPLOADED_FILES_SIZE}
          isDisabled={isLoading}
          error={error?.type === 'attachDoc' ? getErrorMessageForAlert(error) : null}
        />

        <Button
          className={'new-topic-form-button'}
          type={'primary'}
          roleType={'submit'}
          size={'large'}
          fullWidth={true}
          onClick={() => {}}
          isDisabled={isLoading}
        >
          {isLoading ? <Loader /> : i18n.getMessage('messages.button.sendMessage')}
        </Button>
      </form>
    </div>
  );
};

NewTopicForm.propTypes = {
  newTopicData: PropTypes.object.isRequired,
  setNewTopicData: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  error: PropTypes.any,
  clearError: PropTypes.func.isRequired,
  uploadedFiles: PropTypes.array,
  uploadDocuments: PropTypes.func.isRequired,
  removeAttachment: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired
};

export default NewTopicForm;
